import { Form } from 'antd';
import { FormProviderProps } from 'antd/es/form/context';
import { Dayjs } from 'dayjs';
import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  getDefaultTableParams,
  useTableParams,
} from 'business/providers/table-params';
import { DEFAULT_PAGE_SIZE } from 'business/quality-control/constants';
import { RingComplianceStatus } from 'business/quality-control/pages/ring-compliance-report/hooks/data/types';
import { useRingComplianceReportFilterParams } from 'business/quality-control/pages/ring-compliance-report/hooks/use-filter-params';
import { NumberRangeValue } from 'ui/form/number-range-input';

import { ORDER_FIELD, ORDER_TYPE } from '..';

const FILTERS_FORM_NAME = 'ring-compliance-report-filters-form';

export type RingComplianceReportFilters = {
  ringNumberRange: NumberRangeValue;
  status: RingComplianceStatus[];
  complianceDateRange: [Dayjs | null, Dayjs | null];
  approvalDateRange: [Dayjs | null, Dayjs | null];
};

const defaultFilters = {
  ringNumberRange: [null, null],
  status: [],
  complianceDateRange: [null, null],
  approvalDateRange: [null, null],
} as const satisfies RingComplianceReportFilters;

const RingComplianceReportFiltersContext =
  createContext<RingComplianceReportFilters>(defaultFilters);

const RingComplianceReportResetFiltersContext = createContext<() => void>(
  () => {},
);

const RingComplianceReportFiltersProvider = ({
  initialFilters,
  children,
}: PropsWithChildren<{
  initialFilters: RingComplianceReportFilters;
}>) => {
  const [filters, setFilters] =
    useState<RingComplianceReportFilters>(initialFilters);
  const { tableParams, setTableParams } = useTableParams();
  const { updateSearchParams } = useRingComplianceReportFilterParams();

  useEffect(() => {
    updateSearchParams(filters, tableParams);
    // Only the filters/tableParams state should trigger a URL sync, not
    // `updateSearchParams` itself (it changes identity on every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, tableParams]);

  const resetTableParams = () => {
    const defaultTableParams = getDefaultTableParams({
      defaultPageSize: DEFAULT_PAGE_SIZE,
      defaultSortField: ORDER_FIELD,
      defaultSortOrder: ORDER_TYPE,
    });
    setTableParams(defaultTableParams);
  };

  const onFormChange: FormProviderProps['onFormChange'] = (
    formName,
    { forms },
  ) => {
    if (formName === FILTERS_FORM_NAME) {
      const form = forms[formName];

      const values = {
        ...defaultFilters,
        ...form.getFieldsValue(),
      } satisfies RingComplianceReportFilters;

      setFilters(values);
      form.setFieldsValue(values);

      // Reset pagination, order, etc.
      resetTableParams();
    }
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    resetTableParams();
  };

  return (
    <RingComplianceReportFiltersContext.Provider value={filters}>
      <RingComplianceReportResetFiltersContext.Provider value={resetFilters}>
        <Form.Provider onFormChange={onFormChange}>{children}</Form.Provider>
      </RingComplianceReportResetFiltersContext.Provider>
    </RingComplianceReportFiltersContext.Provider>
  );
};

const useRingComplianceReportFiltersFormName = () => FILTERS_FORM_NAME;

const useRingComplianceReportFilters = () => {
  return useContext(RingComplianceReportFiltersContext);
};

const useResetRingComplianceReportFilters = () => {
  return useContext(RingComplianceReportResetFiltersContext);
};

export {
  defaultFilters,
  RingComplianceReportFiltersProvider,
  useRingComplianceReportFiltersFormName,
  useRingComplianceReportFilters,
  useResetRingComplianceReportFilters,
};
